import { zodResolver } from '@hookform/resolvers/zod'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { LabelingContainer } from '~/components/elements/LabelingContainer'
import { StyledTextArea } from '~/components/elements/StyledTextArea'
import { useFormField } from '~/hooks/useFormField'
import { useLogEvent } from '~/hooks/useLogEvent'
import { useNotify } from '~/hooks/useNotify'
import { useNotifyOnError } from '~/hooks/useNotifyOnError'

import { dialogInfoAtom } from '../atoms'
import {
  userRoleNameToLabel,
  userRoleNameToLogEventName,
  userRoleNameToRoleId,
} from '../constants'
import { useAddBrandAccountUsersMutation } from '../hooks'
import { useBrandAccountId } from '../hooks/useBrandAccountId'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'

const schema = z.object({
  emails: z
    .string()
    .transform((value) =>
      value
        .split('\n')
        .map((email) => email.trim())
        .filter((email) => email !== ''),
    )
    .pipe(z.array(z.string().email())),
})
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const AddBrandUserDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  if (dialogInfo?.dialogType !== 'add') {
    throw new Error()
  }

  const brandAccountId = useBrandAccountId()
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const notify = useNotify()
  const notifyError = useNotifyOnError()
  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const logEvent = useLogEvent()

  const addBrandAccountUsersMutation = useAddBrandAccountUsersMutation({
    onSuccess: (data) => {
      // NOTE: 既に存在しているユーザーのみ追加した際はエラー表記にする
      if (data.addBrandAccountUsers?.length === 0) {
        notify({
          style: 'error',
          content:
            '追加できませんでした。すでに登録されている、あるいは指定された権限の付与が禁止されている可能性があります。',
        })
      } else {
        notify({
          style: 'success',
          content: t('user_added', {
            role: t(userRoleNameToLabel[dialogInfo.userRoleName]),
          }),
        })

        // 0件のときは既存ユーザーのみの指定で実際には追加されていないため、成功分岐でのみ記録する
        logEvent(userRoleNameToLogEventName[dialogInfo.userRoleName])
      }

      closeDialogWithRefresh()
    },
    onError: (data) => {
      notifyError(data, {
        400: t('insufficient_user_seats'),
        403: t('no_permission_to_add_users'),
        409: t('insufficient_partner_seats'),
      })

      resetDialogInfo()
    },
  })

  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
  } = useForm<InputValues>({ resolver })
  const emailsField = useFormField(control, 'emails')

  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ emails }) => {
      if (!isDirty) {
        resetDialogInfo()

        return
      }

      if (emails.length === 0) {
        resetDialogInfo()

        return
      }

      addBrandAccountUsersMutation.mutate({
        brandAccountId,
        emails,
        roleId: userRoleNameToRoleId[dialogInfo.userRoleName],
      })
    },
    [
      addBrandAccountUsersMutation,
      brandAccountId,
      dialogInfo.userRoleName,
      isDirty,
      resetDialogInfo,
    ],
  )

  return (
    <ExecuteCancelDialog
      disabledExecute={!isValid}
      executing={false}
      mode="create"
      title={t('user_added', {
        role: t(userRoleNameToLabel[dialogInfo.userRoleName]),
      })}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <LabelingContainer
        label={t(
          'email_address_one_entry_per_line_multiple_entries_can_be_specified_by_separating_them_with_line_breaks',
        )}
      >
        <StyledTextArea
          autoSize={true}
          placeholder={`user1@example.com\nuser2@example.com`}
          rows={3}
          width="100%"
          {...emailsField}
        />
      </LabelingContainer>
    </ExecuteCancelDialog>
  )
}
